import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FormService } from 'src/app/services/form.service';
import { ApiService, Countries } from "../../services/api.service";
import { filter, fromEvent, Subscription, take } from "rxjs";
import { TrackingService } from "../../services/tracking.service";
import { AutofillMonitor } from "@angular/cdk/text-field";

@Component( {
	selector: 'app-moving-from',
	templateUrl: './moving-from.component.html',
	styleUrls: ['./moving-from.component.scss'],
} )
export class MovingFromComponent implements OnInit {

	movingFrom !: UntypedFormGroup
	formGroupName: string = 'movingFrom'
	public countries!: Countries;
	private autofillSubscription: Subscription | undefined;
	postcodeEnabled = true;
	@ViewChild( 'addressInputEl' ) addressInputEl!: ElementRef;

	labelAddress: string = '';
	labelPostcode: string = '';
	fieldOrder: any = [];
	postcodeBusy = false;
	countryFirst = false;

	// Initially true because this is the first step anyway
	enableFields = true;

	constructor(
		public formService: FormService,
		private apiService: ApiService,
		private trackingService: TrackingService,
		private autofillMonitor: AutofillMonitor  ) {
	}

	ngOnInit(): void {

		// formService comes from constructor
		this.movingFrom = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

		this.labelAddress = this.formService.getVariationTranslation( 'label-address');
		this.labelPostcode = this.formService.getVariationTranslation( 'label-postcode');
		this.fieldOrder = this.formService.getFieldOrder();

		// Decide order based on domainData
		this.countryFirst = this.formService.domainData?.countryFirst;

		// Check if we can do the postcode api for this site
		// This checks if we only have a postcode from step0
		if( this.formService.verifyPostcodeApi() ) {

			// Launch postcode based on postcode from step0
			this.doPostcodeApi();
		}

		// Initial check
		this.onCountryChange();

		this.formService.stepChange$.subscribe( currentFormGroupName => {

			// This enables the fields in the current step
			this.enableFields = currentFormGroupName === this.formGroupName;
		} );
	}

	ngAfterViewInit() {

		// Check if we need to focus the address first
		if( this.movingFrom.controls['_initiallyFocusAddress'].value ) {

			setTimeout( () => {
				this.addressInputEl.nativeElement.focus();
			}, 0 )
		}

		this.autofillSubscription = this.autofillMonitor.monitor( document.body ).subscribe(event => {

			// Subscribe to input event to check for value changes after autofill
			const inputElement = event.target as HTMLInputElement;

			fromEvent(inputElement, 'input').pipe(
				take(1),
				filter(() => !!inputElement.value.trim() )
			).subscribe(() => {

				console.log( 'Autofill detected' );

				// Perform your tracking logic here
				this.trackingService.sendGTMEvent( {
					'event': 'autofill detected',
					'eventName': 'autofill',
				} );

				// Unsubscribe from the autofill monitor after the first detection
				// This also eliminates the inner subscribe
				if( this.autofillSubscription ) {
					this.autofillSubscription.unsubscribe();
				}
			} );
		} );
	}

	public onSubmit() {

		this.formService.submitFormGroup( (<UntypedFormGroup>this.movingFrom) );
	}

	onCountryChange() {

		var selectedCountry = this.movingFrom.controls['country'].value;
		// Ireland, Qatar, United-arab-emirates, Hong kong, Curacao
		// Aruba, Cameroon, Ghana, Suriname, Sint maarten
		var excludedCountries = ['IE', 'QA', 'AE', 'HK', 'CW', 'AW', 'CM', 'GH', 'SR', 'SX'];
		var postcodeControl = (<UntypedFormGroup>this.movingFrom).controls['postcode'];

		if( excludedCountries.includes( selectedCountry ) ) {

			// Make un-required and reset value
			postcodeControl.removeValidators( Validators.required );
			postcodeControl.reset();

			// Change field order
			this.fieldOrder = ['address','city','postcode'];

			// Hide postcode field
			this.postcodeEnabled = false;
		}
		else {

			// Make required
			postcodeControl.addValidators( Validators.required );

			// Show postcode field
			this.postcodeEnabled = true;

			// Reset field order
			this.fieldOrder = this.formService.getFieldOrder();
		}

		console.log( 'attempt to get order from order by country ');

		this.fieldOrder = this.formService.getFieldOrderByCountry( selectedCountry );

		// Check if we can enable movingToExtended
		this.formService.checkToEnableMovingToExtended();
	}

	onPostcodeFieldKeyup( $event: any ) {

		// Ignore tab 9
		// Ignore shift 16
		// Ignore arrow keys 37,38,39,40
		if( [9, 16, 37, 38, 39, 40].includes( $event.keyCode ) )
			return;

		// Launch postcode API
		this.doPostcodeApi();
	}

	/**
	 * Handles the call to the postcode API and the results of it
	 * Only for NL
	 */
	public doPostcodeApi( ) {

		if( ! this.formService.domainData?.postcodeApiEnabled || this.postcodeBusy )
			return;

		var movingFrom = this.movingFrom;

		// Get initial postcode
		var postcodeValue = movingFrom.controls['postcode'].value;

		// Clears everything except letters and numbers
		// This is meant for the Dutch postcode format
		postcodeValue = postcodeValue.replace( /[^a-zA-Z0-9]+/g, '' );

		// Check if the country is already set and on a different country than the netherlands
		var countryHasValueAndIsNotNetherlands = movingFrom.controls['country'].value && movingFrom.controls['country'].value.length > 0 && this.formService.verifyCountryCode( movingFrom.controls['country'].value ) !== 'NL';

		// Need at least 6 characters for the postcode API to start
		if( postcodeValue.length < 6 || postcodeValue.length > 7 || countryHasValueAndIsNotNetherlands )
			return;

		// To only have 1 at a time.
		this.postcodeBusy = true;

		// Pass this value to the postcode api
		var postcodeData = this.apiService.getPostCodeData( postcodeValue ).subscribe( { next: response => {

			if( response.status === 'ok' ) {

				var results = response.results[0];

				// Can only be the netherlands
				movingFrom.controls['country'].setValue( 'NL' );
				movingFrom.controls['country'].markAsDirty();

				movingFrom.controls['city'].setValue( results.city );
				movingFrom.controls['address'].setValue( results.street + ' ' );
				this.addressInputEl.nativeElement.focus();
			}

			this.postcodeBusy = false;
			postcodeData.unsubscribe();
		},
		error: error => {

			// This way it can be attempted again
			this.postcodeBusy = false;
		} } );
	}
}
